import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { PAYPAL_CLIENT_ID, CONTACT_PHONE } from './config';
import { fetchAvailability } from './utils/availability';
import { getUpcomingWednesdays, getFirstBookableWednesday, findPromo, getPrintAd, getOnlyIssue } from './utils/pricing';
import { readPromoCodeFromUrl } from './utils/promo';
import { trackEvent, trackPaidOrder } from './utils/analytics';
import { currentInAppBrowser } from './utils/inAppBrowser';
import ProgressBar from './components/ProgressBar';
import Step1OrgType from './components/Step1OrgType';
import Step2Medium from './components/Step1Medium';
import Step3Placement from './components/Step2Placement';
import Step4Duration from './components/Step3Duration';
import Step5Contact from './components/Step4Contact';
import Step6Review from './components/Step5Review';
import Confirmation from './components/Confirmation';
import InvoiceRequested from './components/InvoiceRequested';
import InAppBrowserNotice from './components/InAppBrowserNotice';
import Testimonials from './components/Testimonials';
import './App.css';

// Built per-mount rather than as a module constant: the start date and the promo
// code both depend on when the page was opened and what URL it was opened with.
function makeInitialForm() {
  return {
    // Business is pre-selected so the first screen is never a dead end; step 1
    // discloses the default and non-profits switch with one tap.
    orgType:     'business',   // 'business' | 'nonprofit'
    ein:         '',           // EIN for nonprofits, format XX-XXXXXXX (collected at the contact step)
    medium:      null,
    printAd:     null,
    orientation: null,   // 'horizontal' | 'vertical' | null (single-orientation ads)
    digitalAds:  [],
    digitalSkips: {},   // { [adId]: ['YYYY-MM-DD', …] } — weeks a placement skips (e.g. sold out)
    weeks:       4,
    // Drives the one-time nudge on the weeks selector at the duration step —
    // it stops pulsing as soon as the advertiser touches the control.
    weeksTouched: false,
    // Pre-selected to the soonest issue still open for creative, so the duration
    // step opens on a real run schedule instead of an empty picker.
    startDate:   getFirstBookableWednesday(),
    // Flash-sale code from ?promo=… — null for everyone who didn't follow a sale link.
    promo:       readPromoCodeFromUrl(),
    contact:     { name: '', businessName: '', email: '', phone: '' },
  };
}

// Human-readable names for each step, used in GA4 funnel events.
const STEP_NAMES = {
  1: 'org_type',
  2: 'medium',
  3: 'placement',
  4: 'duration',
  5: 'contact',
  6: 'review',
};

export default function App() {
  const [step, setStep]           = useState(1);
  const [form, setForm]           = useState(makeInitialForm);
  const [confirmed, setConfirmed] = useState(false);
  const [invoiceRequested, setInvoiceRequested] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [availability, setAvailability] = useState({ ok: false });
  // One-line explanation shown on the size step when the form had to drop a
  // size the advertiser picked (e.g. a non-profit-only size after a switch).
  const [sizeNotice, setSizeNotice] = useState(null);

  // Resolves to null for an unknown or expired code, so a stale link shared
  // months later just loads the normal form with no dead-sale messaging.
  const activePromo = findPromo(form.promo);

  // Email/social-app browsers can block the PayPal window; detected once per visit.
  const [inApp] = useState(currentInAppBrowser);

  // GA4 funnel tracking. form_start fires on the very first interaction;
  // field_interaction fires once per step the user actually touches.
  const startedRef = useRef(false);
  const interactedStepsRef = useRef(new Set());

  // Only real, user-generated input counts. Focus, page load and scripted
  // events (email link scanners, prefetchers) never start the funnel.
  function handleInteraction(e) {
    if (e && e.isTrusted === false) return;
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent('form_start');
    }
    if (!interactedStepsRef.current.has(step)) {
      interactedStepsRef.current.add(step);
      trackEvent('field_interaction', { step, step_name: STEP_NAMES[step] });
    }
  }

  // Pull current bookings for the next year of Wednesdays once on load (and on
  // demand before payment). One call covers every date the form can offer.
  const refreshAvailability = useCallback(async () => {
    const data = await fetchAvailability(getUpcomingWednesdays(52));
    setAvailability(data);
  }, []);

  useEffect(() => { refreshAvailability(); }, [refreshAvailability]);

  // Fires once when someone lands on a live sale link. Pairs with the promo
  // fields on form_submit below so redemption rate is visible in GA4.
  useEffect(() => {
    if (activePromo) trackEvent('ad_promo_link_open', { ad_promo_code: activePromo.code });
  }, [activePromo]);

  // Counts in-app-browser visits in GA4, so ad_inapp_browser vs form_submit
  // shows whether those visitors are dropping off at payment.
  useEffect(() => {
    if (inApp) trackEvent('ad_inapp_browser', { ad_inapp_app: inApp.app || 'unknown', ad_inapp_os: inApp.os });
  }, [inApp]);

  function handleInvoiceRequested(data) {
    setOrderData(data);
    setInvoiceRequested(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleConfirm(data) {
    // Fires only after PayPal captures payment. Real orders send form_submit
    // (conversion count) + purchase (revenue); test orders send ad_test_order only.
    trackPaidOrder(form, data, PAYPAL_CLIENT_ID);
    setOrderData(data);
    setConfirmed(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goTo(n) {
    setStep(n);
    // Anchor to the progress bar rather than the page top, so navigating steps
    // doesn't force the user back past the tall header every time.
    const anchor = document.querySelector('.progress-bar');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Single owner of org-type switching: no-op guard, one GA4 event, one state
  // update. Every switch point (step 1, contact-step escape link, review link)
  // goes through here so the "track only real changes" invariant can't drift.
  function changeOrgType(type, source) {
    if (form.orgType === type) return;
    trackEvent('org_type_change', { org_type: type, source });

    // A non-profit-only size can't come along to business rates: drop it,
    // reset the run to the usual default, and send them back to pick a size.
    const printAd = getPrintAd(form.printAd);
    if (type === 'business' && printAd && printAd.nonprofitOnly) {
      setForm(f => ({
        ...f,
        orgType:      type,
        medium:       null,
        printAd:      null,
        orientation:  null,
        digitalAds:   [],
        digitalSkips: {},
        weeks:        4,
        weeksTouched: false,
        startDate:    getFirstBookableWednesday(),
      }));
      setSizeNotice(`The ${printAd.label} is for non-profits only, so please choose another size.`);
      if (step > 2) goTo(2);
      return;
    }

    setForm(f => ({ ...f, orgType: type }));
  }

  function handleDigitalOnly() {
    setForm(f => {
      const next = { ...f, medium: 'digital', printAd: null, digitalAds: [] };
      // Leaving a one-issue special: its locked run date shouldn't stick.
      return getOnlyIssue(f.printAd)
        ? { ...next, weeks: 4, weeksTouched: false, startDate: getFirstBookableWednesday() }
        : next;
    });
    setSizeNotice(null);
    goTo(3);
  }

  if (confirmed) {
    return <Confirmation orderData={orderData} form={form} />;
  }

  if (invoiceRequested) {
    return <InvoiceRequested orderData={orderData} form={form} />;
  }

  return (
    <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD' }}>
      <div className="app-container">
        <header className="app-header">
          <a
            href="https://www.hendersonvillelightning.com"
            target="_blank"
            rel="noopener noreferrer"
            className="logo-link"
            title="Go to www.HendersonvilleLightning.com"
          >
            <img src="/logo.jpg.jpg" alt="Hendersonville Lightning" className="logo-img" />
          </a>
          <h1 className="page-title">Place Your Advertisement</h1>

          <div className="header-stats">
            <div className="header-stat">
              <span className="header-stat-number">5,000+</span>
              <span className="header-stat-label">Weekly Print<br/>Readers</span>
            </div>
            <div className="header-stat-divider" />
            <div className="header-stat">
              <span className="header-stat-number">25,000+</span>
              <span className="header-stat-label">Monthly Online<br/>Readers</span>
            </div>
            <div className="header-stat-divider" />
            <div className="header-stat">
              <span className="header-stat-number">55.7%</span>
              <span className="header-stat-label">Email Newsletter<br/>Open Rate</span>
            </div>
            <div className="header-stat-divider" />
            <div className="header-stat">
              <span className="header-stat-number">2,200+</span>
              <span className="header-stat-label">Paid Annual<br/>Subscribers</span>
            </div>
          </div>

          <p className="header-description">
            The Hendersonville Lightning is an <strong>award-winning weekly newspaper</strong> and 24/7 website
            covering local news, business, and entertainment in Henderson County, NC.{' '}
            <span className="header-description-line2">
              Reaching affluent, engaged readers, it's the trusted way to connect with locals
              who can afford your products and services.
            </span>
          </p>
        </header>

        {activePromo && (
          <div className="promo-banner">
            <span className="promo-banner-tag">{activePromo.label}</span>
            <span className="promo-banner-text">{activePromo.blurb}</span>
          </div>
        )}

        <ProgressBar currentStep={step} />

        <main onPointerDownCapture={handleInteraction} onKeyDownCapture={handleInteraction}>
          {step === 1 && <InAppBrowserNotice info={inApp} variant="arrival" />}
          {step === 1 && (
            <Step1OrgType
              form={form}
              onChangeOrgType={changeOrgType}
              onNext={() => goTo(2)}
            />
          )}
          {step === 2 && (
            <Step2Medium
              form={form}
              setForm={setForm}
              notice={sizeNotice}
              onClearNotice={() => setSizeNotice(null)}
              onNext={() => goTo(3)}
              onBack={() => goTo(1)}
              onDigitalOnly={handleDigitalOnly}
            />
          )}
          {step === 3 && (
            <Step3Placement
              form={form}
              setForm={setForm}
              availability={availability}
              onNext={() => goTo(4)}
              onBack={() => goTo(2)}
            />
          )}
          {step === 4 && (
            <Step4Duration
              form={form}
              setForm={setForm}
              availability={availability}
              onNext={() => goTo(5)}
              onBack={() => goTo(3)}
            />
          )}
          {step === 5 && (
            <Step5Contact
              form={form}
              setForm={setForm}
              onChangeOrgType={changeOrgType}
              onNext={() => goTo(6)}
              onBack={() => goTo(4)}
            />
          )}
          {step === 6 && (
            <Step6Review
              form={form}
              availability={availability}
              onRefreshAvailability={refreshAvailability}
              onBack={() => goTo(5)}
              onSwitchToNonprofit={() => {
                // Safety net for non-profits who sailed through on the business
                // default. Routes through the contact step so the EIN gets collected.
                changeOrgType('nonprofit', 'review');
                goTo(5);
              }}
              onConfirm={handleConfirm}
              onInvoiceRequested={handleInvoiceRequested}
            />
          )}
        </main>

        <Testimonials />

        <footer className="app-footer">
          For questions or advertising assistance, call <a href={CONTACT_PHONE.tel}>{CONTACT_PHONE.display}</a> or email <a href="mailto:ads@hendersonvillelightning.com">ads@hendersonvillelightning.com</a>
          <div className="footer-copyright">
            <a href="https://www.hendersonvillelightning.com" target="_blank" rel="noopener noreferrer">www.HendersonvilleLightning.com</a>
            {' '}&copy; {new Date().getFullYear()} Hendersonville Lightning. All Rights Reserved.
          </div>
        </footer>
      </div>
    </PayPalScriptProvider>
  );
}
